// src/pages/Trend/components/analysis/product-analysis/ColorAnalysisWrapper.js
import React from 'react';
import PropTypes from 'prop-types';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { BBC_STYLE } from '../../../../../styles/chartStyles';
import ChartContainer from '../common/ChartContainer';
import ColorAnalysisChart from './ColorAnalysisChart';

/**
 * Wrapper component for Color Analysis Chart
 * Provides consistent container styling and header
 */
const ColorAnalysisWrapper = ({ data }) => {
  const { translate } = useLanguage();

  if (!data?.primary?.feature_analysis) {
    return (
      <div className="analysis-error">
        {translate('trendPage.productAnalysis.errors.noFeatureData')}
      </div>
    );
  }

  const hasSecondary = !!data.secondary?.feature_analysis;
  const { primary, secondary } = data;

  // Extract color analysis data
  const productColorKey = translate('trendPage.productAnalysis.features.productColor');
  const colorAnalysis = primary.feature_analysis[productColorKey] ||
                       primary.feature_analysis['제품 컬러'];
  const secondaryColorAnalysis = hasSecondary ?
    (secondary.feature_analysis[productColorKey] ||
     secondary.feature_analysis['제품 컬러']) : null;

  if (!colorAnalysis?.labels || !colorAnalysis?.values) {
    return (
      <div className="analysis-error">
        {translate('trendPage.productAnalysis.errors.noColorData')}
      </div>
    );
  }

  // Calculate narrative for color analysis
  const { labels, values: primaryValues } = colorAnalysis;
  const primaryMax = Math.max(...primaryValues);
  const primaryMaxIndex = primaryValues.indexOf(primaryMax);
  const primaryMaxLabel = labels[primaryMaxIndex];
  const primaryTotal = primaryValues.reduce((sum, val) => sum + val, 0);
  const primaryMaxPercent = ((primaryMax / primaryTotal) * 100).toFixed(0);

  const getNarrative = () => {
    if (!hasSecondary || !secondaryColorAnalysis) {
      return (
        <span>
          <span style={{
            fontWeight: '600',
            color: BBC_STYLE.HIGHLIGHT_COLOR,
            borderBottom: `2px solid ${BBC_STYLE.HIGHLIGHT_COLOR}`,
            paddingBottom: '1px'
          }}>
            {primaryMaxLabel}
          </span>
          {' '}{translate('trendPage.productAnalysis.narratives.feature.mostSelected')}{' '}
          <span style={{
            fontWeight: '600',
            backgroundColor: 'rgba(250, 171, 24, 0.15)',
            padding: '0 4px',
            borderRadius: '2px'
          }}>
            {primaryMaxPercent}%
          </span>
          {' '}{translate('trendPage.productAnalysis.narratives.feature.share')}
        </span>
      );
    }

    // With secondary data
    const secondaryValues = secondaryColorAnalysis.values || [];
    const secondaryMax = Math.max(...secondaryValues);
    const secondaryMaxIndex = secondaryValues.indexOf(secondaryMax);
    const secondaryMaxLabel = labels[secondaryMaxIndex];
    const secondaryTotal = secondaryValues.reduce((sum, val) => sum + val, 0);
    const secondaryMaxPercent = ((secondaryMax / secondaryTotal) * 100).toFixed(0);

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        <span>
          {primaryMaxLabel === secondaryMaxLabel ? (
            <>
              <span style={{
                fontWeight: '600',
                color: BBC_STYLE.HIGHLIGHT_COLOR
              }}>
                {primaryMaxLabel}
              </span>
              {' '}{translate('trendPage.productAnalysis.narratives.feature.ranksBothFilters')}
            </>
          ) : translate('trendPage.productAnalysis.narratives.feature.differentPreferences')}
        </span>
        <span style={{ margin: '0 4px', color: '#D0D0D0', fontWeight: '300' }}>｜</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: BBC_STYLE.PRIMARY_COLOR,
            display: 'inline-block'
          }} />
          <span style={{ fontWeight: '500', color: BBC_STYLE.PRIMARY_COLOR }}>
            {primaryMaxLabel}
          </span>
          <span style={{ fontSize: '0.85rem' }}>({primaryMaxPercent}%)</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: BBC_STYLE.SECONDARY_COLOR,
            display: 'inline-block'
          }} />
          <span style={{ fontWeight: '500', color: BBC_STYLE.SECONDARY_COLOR }}>
            {secondaryMaxLabel}
          </span>
          <span style={{ fontSize: '0.85rem' }}>({secondaryMaxPercent}%)</span>
        </span>
      </div>
    );
  };

  return (
    <ChartContainer autoHeight={false} height="670px">
      <div className="feature-analysis-chart" style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden'
      }}>
        {/* Header with title and narrative - compact */}
        <h3 style={{
          fontSize: '0.85rem',
          fontWeight: '600',
          color: BBC_STYLE.TEXT_COLOR,
          margin: '0 0 3px 0',
          flexShrink: 0
        }}>
          {productColorKey}
        </h3>
        <p style={{
          fontSize: '0.7rem',
          color: BBC_STYLE.LIGHT_TEXT,
          margin: '0 0 8px 0',
          fontWeight: '400',
          lineHeight: '1.3',
          flexShrink: 0
        }}>
          {getNarrative()}
        </p>

        {/* Color Analysis Content - Scrollable */}
        <div className="feature-tab-content" style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>
          <ColorAnalysisChart
            primaryData={colorAnalysis}
            secondaryData={secondaryColorAnalysis}
            hasSecondary={hasSecondary}
          />
        </div>
      </div>
    </ChartContainer>
  );
};

ColorAnalysisWrapper.propTypes = {
  data: PropTypes.shape({
    primary: PropTypes.shape({
      feature_analysis: PropTypes.objectOf(
        PropTypes.shape({
          labels: PropTypes.arrayOf(PropTypes.string),
          values: PropTypes.arrayOf(PropTypes.number)
        })
      )
    }),
    secondary: PropTypes.shape({
      feature_analysis: PropTypes.objectOf(
        PropTypes.shape({
          labels: PropTypes.arrayOf(PropTypes.string),
          values: PropTypes.arrayOf(PropTypes.number)
        })
      )
    })
  })
};

export default ColorAnalysisWrapper;
